
import React, { useState, useRef, useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { isCliente, isEspecialista, getCurrentUser } from "../../utils/auth";
import ReportePdf from './ReportePdf';
import ErrorModal from './ErrorModal';
import { api } from '../../services/api';
import axios from 'axios';

ChartJS.register(ArcElement, Tooltip, Legend);

// Base que será /api/dmarc en producción
const API_BASE = process.env.REACT_APP_API_BASE || "/api/dmarc";
// Para el PDF (endpoint /reports/dmarc.pdf) necesitamos la raíz sin /dmarc
const API_ROOT = (process.env.REACT_APP_API_BASE || "/api/dmarc").replace(
  /\/dmarc\/?$/,
  "",
);

// --- UTILIDADES ---

const formatCompact = (n) => {
  const num = n || 0;
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(num % 1_000_000 ? 1 : 0) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(num % 1_000 ? 1 : 0) + "k";
  return String(num);
};

// --- SUB-COMPONENTES ESTILIZADOS ---

const StatCard = ({ title, children, className = "" }) => (
  <div className={`bg-white dark:bg-slate-900 p-5 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm transition-all duration-500 ${className}`}>
    <h3 className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-2">{title}</h3>
    {children}
  </div>
);

const ComplianceBar = ({ pct }) => {
  const p = Math.max(0, Math.min(100, Number(pct || 0)));
  return (
    <div className="w-full max-w-[100px]">
      <div className="w-full h-2 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-cyan-500"
          style={{ width: `${p}%` }}
        />
      </div>
      <div className="text-right text-[10px] font-bold text-gray-500 dark:text-cyan-400 mt-1">
        {p.toFixed(1)}%
      </div>
    </div>
  );
};

const CountryFlagImg = ({ iso }) => {
  const cc = (iso || "").trim().toLowerCase();
  if (!cc || cc.length !== 2) return <span className="text-xs text-gray-300 dark:text-slate-700">-</span>;
  const src = `https://flagcdn.com/${cc}.svg`;
  return (
    <div className="flex items-center gap-2">
      <img src={src} alt={cc} className="w-5 h-auto rounded-sm opacity-90 shadow-sm flex-shrink-0" />
      <span className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase truncate">{cc}</span>
    </div>
  );
};

const CloudPill = ({ vendor }) => (
  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight bg-cyan-100 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border border-cyan-200/30 dark:border-cyan-500/20 whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
    {vendor || "-"}
  </span>
);

const BoolPill = ({ value }) => {
  const isPass = value === true || value === 'pass' || value === 'true' || value === '1';
  return (
    <div className="flex justify-center">
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
        isPass 
          ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10' 
          : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10'
      }`}>
        {isPass ? 'PASS' : 'FAIL'}
      </span>
    </div>
  );
};

const SortableHeader = ({ label, column, sortConfig, onSort, className = "" }) => (
  <th 
    className={`px-3 py-3 text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-tighter cursor-pointer hover:bg-gray-100/50 dark:hover:bg-slate-800 transition-colors group ${className}`}
    onClick={() => onSort(column)}
  >
    <div className="flex items-center justify-between">
      <span className="truncate pr-1">{label}</span>
      <span className="opacity-0 group-hover:opacity-100 transition-opacity">
        {sortConfig.field === column ? (
          <i className={`fas ${sortConfig.ascending ? 'fa-sort-up' : 'fa-sort-down'} text-cyan-500`}></i>
        ) : (
          <i className="fas fa-sort text-gray-300 dark:text-slate-700"></i>
        )}
      </span>
    </div>
  </th>
);

const normalizeIpsOK = (rows = []) =>
  rows.map((r) => ({
    recordid: r.recordid ?? r.RecordID ?? r.id ?? null,
    sourceip: r.sourceip ?? r.SourceIP ?? r.ip ?? null,
    dominio: r.dominio ?? r.domain ?? r.Domain ?? null,
    reportorgname: r.reportorgname ?? r.ReportOrgName ?? r.origen ?? null,
    count: Number(r.count ?? r.Count ?? r.total_correos ?? 0),
    dkimaligned:
      r.dkimaligned ?? r.DKIMAligned ?? r.dkim_alineado ?? r["Alineación DKIM"],
    spfaligned:
      r.spfaligned ?? r.SPFAligned ?? r.spf_alineado ?? r["Alineación SPF"],
    disposition: r.disposition ?? r.resultado_dmarc ?? null,
    authtype: r.authtype ?? r.AuthType ?? r["Tipo Evaluación"] ?? null,
    result: r.result ?? r.Result ?? r["Resultado Auth"] ?? null,
    selector: r.selector ?? r.Selector ?? null,
    cloud_guess: r.cloud_guess ?? r.cloud ?? r.vendor ?? null,
    country_iso: r.country_iso ?? r.country ?? r.countryCode ?? null,
  }));

const getNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const normalizeTotalMensajes = (t) => {
  if (typeof t === "number") return t;
  if (t && typeof t === "object") {
    return getNumber(
      t.total ?? t.Total ?? t.total_mensajes ?? t.TotalMensajes ?? t.count,
    );
  }
  return 0;
};

const formatDateYYYYMMDD = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getOneWeekAgoDateString = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return formatDateYYYYMMDD(d);
};

const getTodayDateString = () => {
  return formatDateYYYYMMDD(new Date());
};


const getDefaultFiltersFromUser = () => {
  const user = getCurrentUser(); // viene del token en localStorage
  const hoy = new Date();
  const haceUnaSemana = new Date();
  haceUnaSemana.setDate(hoy.getDate() - 7);

  const defaultDesde = formatDateYYYYMMDD(haceUnaSemana);
  const defaultHasta = formatDateYYYYMMDD(hoy);

  let defaultDominio = "";

  if (user) {
    const rol = String(user.rol || "").toLowerCase();

    let possibleLists =
      user.dominios || user.domains || user.dominios_asignados || [];

    if (!Array.isArray(possibleLists) && typeof possibleLists === "string") {
      try {
        possibleLists = JSON.parse(possibleLists);
      } catch {
        possibleLists = [];
      }
    }

    if (rol === "cliente") {
      if (Array.isArray(possibleLists) && possibleLists.length > 0) {
        defaultDominio = String(possibleLists[0]).toLowerCase();
      } else if (user.dominio) {
        defaultDominio = String(user.dominio).toLowerCase();
      }
    } else if (rol === "especialista") {
      defaultDominio = "bombesoluciones.com";
    } else {
      if (Array.isArray(possibleLists) && possibleLists.length > 0) {
        defaultDominio = String(possibleLists[0]).toLowerCase();
      }
    }
  }

  return {
    dominio: defaultDominio,
    desde: defaultDesde,
    hasta: defaultHasta,
  };
};

const DmarcDashboard = () => {
  const [dominio, setDominio] = useState('');
  const [desde, setDesde] = useState(() => getOneWeekAgoDateString());
  const [hasta, setHasta] = useState(() => getTodayDateString());
  const [loading, setLoading] = useState(false);
  const [swapped, setSwapped] = useState(false);
  const [error, setError] = useState("");

  const [pageOk, setPageOk] = useState(1);
  const [pageBad, setPageBad] = useState(1);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });
  const PAGE_SIZE = 10;

  const defaultsRef = useRef(getDefaultFiltersFromUser());

  const [totales, setTotales] = useState(null);
  const [dkim, setDkim] = useState(null);
  const [spf, setSpf] = useState(null);
  const [estadisticas, setEstadisticas] = useState([]);
  const [cumplimientoNube, setCumplimientoNube] = useState([]);
  const [ipsInvalidas, setIpsInvalidas] = useState([]);
  const [ipsValidas, setIpsValidas] = useState([]);
  const [politicaDominio, setPoliticaDominio] = useState([]);

  const [sortConfigOk, setSortConfigOk] = useState({ field: 'count', ascending: false });
  const [sortConfigBad, setSortConfigBad] = useState({ field: 'mensajes_fail', ascending: false });

  const [selectedSenderFuente, setSelectedSenderFuente] = useState('');
  const [selectedIp, setSelectedIp] = useState('');

  const [isNubeDrawerOpen, setIsNubeDrawerOpen] = useState(false);

  const [exportingOk, setExportingOk] = useState(false);
  const [exportingBad, setExportingBad] = useState(false);
  
  const [rawIpsValidas, setRawIpsValidas] = useState([]);
  const [rawIpsInvalidas, setRawIpsInvalidas] = useState([]);

  const filteredIpsOk = useMemo(() => {
    if (selectedSenderFuente !== '') {
      return ipsValidas.filter(ip => {
        if (selectedSenderFuente === "Other") {
          return ip.cloud_guess === "Unknown";
        } else {
          return ip.cloud_guess === selectedSenderFuente;
        }
      });
    }

    if (selectedIp !== '') {
     return ipsValidas.filter(ip => ip.sourceip.trim().match("^" + selectedIp));
    }

    return ipsValidas;
  }, [ipsValidas, selectedSenderFuente, selectedIp]);

  const filteredIpsBad = useMemo(() => {
    if (selectedSenderFuente !== '') {
      return ipsInvalidas.filter(ip => ip.cloud_guess === selectedSenderFuente);
    }

    if (selectedIp !== '') {
     return ipsInvalidas.filter(ip => ip.ip.trim().match("^" + selectedIp));
    }

    return ipsInvalidas;

  }, [ipsInvalidas, selectedSenderFuente, selectedIp]);

  
  const sortedAndPaginatedOk = useMemo(() => {
    const sorted = [...filteredIpsOk].sort((a, b) => {
      const field = sortConfigOk.field;
      const valA = a[field] ?? '';
      const valB = b[field] ?? '';
      if (valA < valB) return sortConfigOk.ascending ? -1 : 1;
      if (valA > valB) return sortConfigOk.ascending ? 1 : -1;
      return 0;
    });
    const start = (pageOk - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [filteredIpsOk, sortConfigOk, pageOk]);

  const sortedAndPaginatedBad = useMemo(() => {
    const sorted = [...filteredIpsBad].sort((a, b) => {
      const field = sortConfigBad.field;
      const valA = a[field] ?? '';
      const valB = b[field] ?? '';
      if (valA < valB) return sortConfigBad.ascending ? -1 : 1;
      if (valA > valB) return sortConfigBad.ascending ? 1 : -1;
      return 0;
    });
    const start = (pageBad - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [filteredIpsBad, sortConfigBad, pageBad]);

  const handleSortOk = (field) => {
    setSortConfigOk(prev => ({ field, ascending: prev.field === field ? !prev.ascending : true }));
    setPageOk(1);
  };

  const handleSortBad = (field) => {
    setSortConfigBad(prev => ({ field, ascending: prev.field === field ? !prev.ascending : true }));
    setPageBad(1);
  };

  const normalizeParams = () => {
    const dom = (dominio || "").trim().toLowerCase();

    // Fechas tal como las ve el usuario
    let d = (desde || "").trim();
    let h = (hasta || "").trim();

    // Si el usuario NO puso fechas, usamos el rango por defecto (últimos 7 días)
    const def = defaultsRef.current || getDefaultFiltersFromUser();

    if (!d) {
      d = def.desde;
      setDesde(d);
    }

    if (!h) {
      h = def.hasta;
      setHasta(h);
    }

    const d1 = new Date(d);
    const d2 = new Date(h);

    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
      return {
        dominio: dom,
        desde: d,
        hasta: h,
      };
    }

    if (d1 > d2) {
      setDesde(h);
      setHasta(d);
      setSwapped(true);
      return {
        dominio: dom,
        desde: h,
        hasta: d,
      };
    }

    setSwapped(false);
    return {
      dominio: dom,
      desde: d,
      hasta: h,
    };
  };

  const totalMensajes = normalizeTotalMensajes(totales);

  const totalPagesOk = useMemo(() => Math.max(1, Math.ceil(filteredIpsOk.length / PAGE_SIZE)), [filteredIpsOk]);
  const totalPagesBad = useMemo(() => Math.max(1, Math.ceil(filteredIpsBad.length / PAGE_SIZE)), [filteredIpsBad]);


  const fetchAll = async () => {
    var params = normalizeParams();
    if (!params.dominio) {
      setErrorModal({ isOpen: true, message: 'Debe ingresar un dominio válido para realizar la consulta.' });
      return;
    }

    if (!params.desde) {
      setErrorModal({ isOpen: true, message: 'Debe seleccionar una fecha de inicio para realizar una consulta.' });
    }

    if (!params.hasta) {
      setHasta(Date.now());
      params.hasta = Date.now();
    }

    setLoading(true);

    const req = async (path, extraConfig = {}) =>
      await api.get(path, {
        params,
        ...extraConfig,
      });

    try {
      const results = await Promise.allSettled([
        req("/totales"),
        req("/dmarc_dkim"),
        req("/dmarc_spf"),
        req("/estadisticas_dominios"),
        req("/ips_invalidas", {
          timeout: 120000,
        }),
        req("/ips_que_pasaron_dmarc", {
          timeout: 120000,
        }),
        req("/cumplimiento_por_nube"),
        req("/published-policy"),
      ]);

      const errs = [];

      // 0) Totales
      if (results[0].status === "fulfilled") {
        setTotales(results[0].value.data);
      } else {
        errs.push("totales");
        setTotales({
          total: 0,
        });
      }

      // 1) DKIM
      if (results[1].status === "fulfilled") {
        setDkim(results[1].value.data);
      } else {
        errs.push("dmarc_dkim");
        setDkim({
          total_dkim_pass_alineado: 0,
        });
      }

      // 2) SPF
      if (results[2].status === "fulfilled") {
        setSpf(results[2].value.data);
      } else {
        errs.push("dmarc_spf");
        setSpf({
          total_spf_pass_alineado: 0,
        });
      }

      // 3) Estadísticas
      if (results[3].status === "fulfilled") {
        const es = results[3].value.data;
        setEstadisticas(Array.isArray(es) ? es : es || []);
      } else {
        errs.push("estadisticas_dominios");
        setEstadisticas([]);
      }

      // 4) IPs inválidas
      if (results[4].status === "fulfilled") {
        const inv = results[4].value.data;
        setIpsInvalidas(Array.isArray(inv) ? inv : inv || []);
      } else {
        errs.push("ips_invalidas");
        setIpsInvalidas([]);
      }

      // 5) IPs que pasaron
      if (results[5].status === "fulfilled") {
        const ok = results[5].value.data;
        const ipsOKRows = Array.isArray(ok) ? ok : ok?.rows || ok || [];
        setIpsValidas(normalizeIpsOK(ipsOKRows));
      } else {
        errs.push("ips_que_pasaron_dmarc");
        setIpsValidas([]);
      }

      // 6) Cumplimiento por nube
      if (results[6].status === "fulfilled") {
        const arr = results[6].value.data || [];
        const sorted = [...arr].sort(
          (a, b) =>
            Number(b.total_mensajes || 0) - Number(a.total_mensajes || 0),
        );
        setCumplimientoNube(sorted);
      } else {
        errs.push("cumplimiento_por_nube");
        setCumplimientoNube([]);
      }

      if (results[7].status === 'fulfilled') {
        const arr = results[7].value.data;
        setPoliticaDominio(arr);
      } else {
        setPoliticaDominio([]);
      }

      if (errs.length > 0) {
        if (results.some(r => {
            if (r.status === 'fulfilled') {
              return r.value?.status === 403;
            } else {
              const err = r.reason;
              return axios.isAxiosError(err) && err.response?.status === 403;
            }
          })) {
           // setErrorModal({ isOpen: true, message: `Acceso denegado al dominio '${dominio}. Intente iniciar sesión de nuevo.'`});
          }
      }
      setPageOk(1);
      setPageBad(1);
    } catch (e) {
      console.error(e);

    } finally {
      setLoading(false);
    }
  };

  const exportToCsv = (filename, headers, rows) => {
    if (!rows || rows.length === 0) return;

    const escapeCsv = (val) => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.map(escapeCsv).join(','),
      ...rows.map(row => row.map(escapeCsv).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportIpsOk = () => {
    const headers = ['Fuente', 'RID', 'IP', 'Dominio', 'País', 'Mensajes', 'DKIM Alineado', 'SPF Alineado', 'Disposición', 'Auth', 'Resultado', 'Selector'];
    const rows = filteredIpsOk.map(ip => [
      ip.cloud_guess ?? '',
      ip.recordid ?? '',
      ip.sourceip ?? '',
      ip.dominio ?? '',
      ip.country_iso ?? '',
      ip.count ?? 0,
      ip.dkimaligned ? 'Sí' : 'No',
      ip.spfaligned ? 'Sí' : 'No',
      ip.disposition ?? '',
      ip.authtype ?? '',
      ip.result ?? '',
      ip.selector ?? '-'
    ]);
    const dateSuffix = desde && hasta ? `_${desde}_a_${hasta}` : '';
    const domainSuffix = dominio ? `_${dominio}` : '';
    exportToCsv(`ips_validas${domainSuffix}${dateSuffix}.csv`, headers, rows);
  };

  const handleExportIpsBad = () => {
    const headers = ['Fuente', 'País', 'IP', 'Dominio', 'Remitente', 'Organización', 'Mensajes'];
    const rows = filteredIpsBad.map(ip => [
        ip.cloud_guess ?? '',
        ip.country_iso ?? '',
        ip.ip.trim() ?? '',
        ip.header_from ?? '',
        ip.sending_domain ?? '',
        ip.fuente ?? '',
        ip.mensaje_fail ?? ''
    ]);
    const dateSuffix = desde && hasta ? `_${desde}_a_${hasta}` : '';
    const domainSuffix = dominio ? `_${dominio}` : '';
    exportToCsv(`ips_invalidas_alertas${domainSuffix}${dateSuffix}.csv`, headers, rows);
  };

  const porcentajeDmarc = React.useMemo(() => {
      const row0 =
        Array.isArray(estadisticas) && estadisticas.length
          ? estadisticas[0]
          : null;
      const val = Number(row0?.porcentaje_pass_dmarc);
      return Number.isFinite(val) ? val : 0;
    }, [estadisticas]);

   const pieData = useMemo(() => ({
    labels: ['Pass', 'Fail'],
    datasets: [{
      data: [porcentajeDmarc, Math.max(0, 100 - porcentajeDmarc)],
      backgroundColor: ['#06b6d4', '#334155'],
      hoverBackgroundColor: ['#0891b2', '#475569'],
      borderWidth: 0,
    }]
  }), [porcentajeDmarc]);

    const pieOptions = {
    cutout: '75%',
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true }
    },
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };

    const toggleFuenteFilter = (domain) => {
    if (selectedSenderFuente === domain) {
      setSelectedSenderFuente('');
    } else {
      setSelectedSenderFuente(domain);
      setPageOk(1);
      setPageBad(1);
    }
  };

  // --- CSV Export Helper Functions ---
  const getAuthHeaders = () => {
    const token = 
      localStorage.getItem('token') || 
      localStorage.getItem('access_token') || 
      sessionStorage.getItem('token') ||
      sessionStorage.getItem('access_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const convertToCsv = (rows) => {
    if (!rows || rows.length === 0) return '';
    const headers = Array.from(new Set(rows.flatMap(r => Object.keys(r))));
    const escapeCell = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };
    const headerLine = headers.map(h => escapeCell(h)).join(',');
    const rowLines = rows.map(r => headers.map(h => escapeCell(r[h])).join(','));
    return [headerLine, ...rowLines].join('\n');
  };

  const downloadCsv = (content, filename) => {
    const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export IPs Válidas: fetch /detalle_recordid_dmarc
  const handleExportCsvOk = async () => {
    if (filteredIpsOk.length === 0) return;
    setExportingOk(true);
    try {
      const headers = getAuthHeaders();
      const allDetailedRows = [];

      for (const row of filteredIpsOk) {
        const recordid = row.recordid;
        let fetchedData = null;

        if (recordid) {
          try {
            let res = await api.get(`/detalle_recordid_dmarc?recordid=${encodeURIComponent(recordid)}`, { headers });
            if (res.data) {
              fetchedData = res.data;
            }
          } catch (err) {
            console.warn(`[CSV Export OK] Error fetching recordid ${recordid}:`, err);
          }
        }

        if (fetchedData) {
          if (Array.isArray(fetchedData)) {
            allDetailedRows.push(...fetchedData);
          } else if (typeof fetchedData === 'object') {
            allDetailedRows.push(fetchedData);
          }
        } else {
          // Fallback if detail fetch fails/unimplemented
          allDetailedRows.push({
            recordid: row.recordid || '',
            sourceip: row.sourceip || row.ip || '',
            dominio: row.dominio || '',
            reportorgname: row.reportorgname || '',
            count: row.count || 0,
            dkimaligned: row.dkimaligned,
            spfaligned: row.spfaligned,
            disposition: row.disposition || '',
            authtype: row.authtype || '',
            result: row.result || '',
            country_iso: row.country_iso || ''
          });
        }
      }

      const csvStr = convertToCsv(allDetailedRows);
      const filename = `ips_validas_${dominio || 'todas'}_${desde}_${hasta}.csv`;
      downloadCsv(csvStr, filename);
    } catch (err) {
      console.error('Error al exportar CSV de IPs válidas:', err);
      setErrorModal({ isOpen: true, message: 'Ocurrió un error al generar el archivo CSV de IPs válidas.' });
    } finally {
      setExportingOk(false);
    }
  };

  // Export IPs Inválidas: fetch /detalle_ip_no_dmarc
  const handleExportCsvBad = async () => {
    if (filteredIpsBad.length === 0) return;
    setExportingBad(true);
    try {
      const headers = getAuthHeaders();
      const allDetailedRows = [];

      for (const row of filteredIpsBad) {
        const ipStr = (row.ip || row.sourceip || '').trim();
        const domStr = dominio || row.dominio || '';
        let fetchedData = null;

        if (ipStr) {
          const queryParams = new URLSearchParams({
            ip: ipStr,
            dominio: domStr,
            desde: desde || '',
            hasta: hasta || ''
          });

          try {
            let res = await api.get(`/detalle_ip_no_dmarc?${queryParams.toString()}`, { headers });
            if (res.data) {
              fetchedData = res.data;
            }
          } catch (err) {
            console.warn(`[CSV Export BAD] Error fetching invalid IP ${ipStr}:`, err);
          }
        }

        if (fetchedData) {
          if (Array.isArray(fetchedData)) {
            allDetailedRows.push(...fetchedData);
          } else if (typeof fetchedData === 'object') {
            allDetailedRows.push(fetchedData);
          }
        } else {
          // Fallback if detail fetch fails/unimplemented
          allDetailedRows.push({
            sourceip: ipStr,
            header_from: row.header_from || row.dominio || '',
            dominio: domStr,
            reportorgname: row.reportorgname || '',
            count: row.count || 0,
            dkimaligned: row.dkimaligned,
            spfaligned: row.spfaligned,
            disposition: row.disposition || '',
            authtype: row.authtype || '',
            result: row.result || '',
            country_iso: row.country_iso || ''
          });
        }
      }

      const csvStr = convertToCsv(allDetailedRows);
      const filename = `ips_invalidas_${dominio || 'todas'}_${desde}_${hasta}.csv`;
      downloadCsv(csvStr, filename);
    } catch (err) {
      console.error('Error al exportar CSV de IPs inválidas:', err);
      setErrorModal({ isOpen: true, message: 'Ocurrió un error al generar el archivo CSV de IPs inválidas.' });
    } finally {
      setExportingBad(false);
    }
  };

return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Filtros */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-100 dark:border-slate-800 flex flex-wrap items-end gap-4 shadow-sm transition-colors">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">Dominio Consultar</label>
          <input
            type="text"
            className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-xl text-sm bg-transparent dark:text-slate-200 outline-none focus:ring-2 focus:ring-cyan-500/50"
            placeholder="ejemplo.com"
            value={dominio}
            onChange={(e) => setDominio(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-36">
          <label className="block text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">Fecha Inicio</label>
          <input type="date"
                 className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-xl text-sm bg-transparent dark:text-slate-200 dark:[color-scheme:dark]" value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div className="w-full sm:w-36">
          <label className="block text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">Fecha Fin</label>
          <input type="date"
                 className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-xl text-sm bg-transparent dark:text-slate-200 dark:[color-scheme:dark]" value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {fetchAll()}}
            disabled={loading}
            className="bg-cyan-600 hover:bg-cyan-700 text-white px-5 py-2 rounded-xl font-bold text-sm h-[42px] flex items-center gap-2 transition active:scale-95 shadow-lg shadow-cyan-100 dark:shadow-none"
          >
            {loading ? <i className="fas fa-spinner animate-spin"></i> : <i className="fas fa-search"></i>}
            <span className="whitespace-nowrap">Consultar</span>
          </button>
          
          <ReportePdf 
            dominio={dominio} 
            desde={desde} 
            hasta={hasta}
            apiRoot={API_ROOT}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard title="Mensajes Totales">
          <div className="text-3xl font-black text-gray-800 dark:text-slate-100">
            {totalMensajes ? totalMensajes.toLocaleString() : '---'}
          </div>
          <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 mt-1 uppercase tracking-tighter">
            {loading ? 'Consultando...' : 'Periodo seleccionado'}
          </p>
        </StatCard>
        
        <StatCard title="Cumplimiento DKIM">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-black text-purple-600 dark:text-purple-400">
              {dkim ? dkim.total_dkim_pass_alineado.toLocaleString() : '---'}
            </span>
            {dkim && totalMensajes && (
              <span className="text-xs text-gray-400 dark:text-slate-500 font-bold uppercase tracking-tighter">
                ({((dkim.total_dkim_pass_alineado / totalMensajes) * 100).toFixed(1)}%)
              </span>
            )}
          </div>
          <div className="w-full bg-gray-100 dark:bg-slate-800 h-2 rounded-full mt-3">
            <div className="bg-purple-500 h-full rounded-full transition-all duration-1000" style={{ width: dkim && totalMensajes ? `${(dkim.total_dkim_pass_alineado / totalMensajes) * 100}%` : '0%' }}></div>
          </div>
        </StatCard>

        <StatCard title="Cumplimiento SPF">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-black text-emerald-600 dark:text-emerald-400">
              {spf ? spf.total_spf_pass_alineado.toLocaleString() : '---'}
            </span>
            {spf && totalMensajes && (
              <span className="text-xs text-gray-400 dark:text-slate-500 font-bold uppercase tracking-tighter">
                ({((spf.total_spf_pass_alineado / totalMensajes) * 100).toFixed(1)}%)
              </span>
            )}
          </div>
          <div className="w-full bg-gray-100 dark:bg-slate-800 h-2 rounded-full mt-3">
            <div className="bg-emerald-500 h-full rounded-full transition-all duration-1000" style={{ width: spf && totalMensajes ? `${(spf.total_spf_pass_alineado / totalMensajes) * 100}%` : '0%' }}></div>
          </div>
        </StatCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <StatCard title="Mensajes Totales" className="lg:col-span-2 flex items-center gap-6">
          <div className="flex-1">
            <div className="text-4xl font-black text-gray-800 dark:text-slate-100 mb-1">
              {totales?.total?.toLocaleString() ?? '---'}
            </div>
            <p className="text-[10px] font-bold text-cyan-500 uppercase tracking-widest">
              {loading ? 'Consultando...' : 'Volumen'}
            </p>
          </div>
          <div className="w-28 h-28 relative flex-shrink-0">
            {totales ? (
              <Pie 
                key={`pie-${porcentajeDmarc}`} // Key dinámica para forzar re-render si el valor cambia drásticamente
                data={pieData} 
                options={pieOptions} 
              />
            ) : (
              <div className="absolute inset-0 border-4 border-dashed border-gray-100 dark:border-slate-800 rounded-full animate-spin-slow"></div>
            )}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-xs font-black text-gray-800 dark:text-slate-100">{porcentajeDmarc}%</span>
            </div>
          </div>
        </StatCard>


        <StatCard className='lg:col-span-2 flex items-center gap-6'>
            <div className="overflow-x-auto">
              <table className="w-full text-center border-collapse table-fixed">
                <colgroup>
                  <col className='w-1/5' />
                  <col className='w-1/5' />
                  <col className='w-1/5' />
                  <col className='w-1/5' />
                  <col className='w-1/5' />
                </colgroup>
                <thead>
                  <tr className="bg-gray-50/50 dark:bg-slate-800/30">
                    <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">DKIM</th>
                    <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">SPF</th>
                    <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">P</th>
                    <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">SP</th>
                    <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">PCT</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
                  <tr>
                    <td className='px-5 py-3.5 font-mono text-gray-700 dark:text-slate-200'>{politicaDominio[0]?.adkim ?? '---'}</td>
                    <td className='px-5 py-3.5 font-mono text-gray-700 dark:text-slate-200'>{politicaDominio[0]?.aspf ?? '---'}</td>
                    <td className='px-5 py-3.5 font-mono text-gray-700 dark:text-slate-200'>{politicaDominio[0]?.p ?? '---'}</td>
                    <td className='px-5 py-3.5 font-mono text-gray-700 dark:text-slate-200'>{politicaDominio[0]?.sp ?? '---'}</td>
                    <td className='px-5 py-3.5 font-mono text-gray-700 dark:text-slate-200'>{politicaDominio[0]?.pct ?? '---'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
        </StatCard>
        <div className="lg:col-span-4 bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">


      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Tabla Cumplimiento por Nube */}
        <div className="xl:col-span-4 bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50 dark:border-slate-800 flex justify-between items-center bg-gray-50/20 dark:bg-slate-800/20">
            <h3 className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest">Cumplimiento por nube</h3>
            <i className="fas fa-cloud text-gray-300 text-sm"></i>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse table-fixed">
              <colgroup>
                <col className="w-1/4" />
                <col className="w-1/4" />
                <col className="w-1/4" />
                <col className="w-1/4" />
              </colgroup>
              <thead>
                <tr className="bg-gray-50/50 dark:bg-slate-800/30">
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">Proveedor</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">Dominio</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">Volumen</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">Cumplimiento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
                {cumplimientoNube.length > 0 ? (
                  (isNubeDrawerOpen ? cumplimientoNube : cumplimientoNube.slice(0, 5)).map((row, i) => (
                    <tr
                      key={i}
                      onClick={() => toggleFuenteFilter(row.cloud_guess)}
                      className={`px-5 py-4 cursor-pointer transition-all ${
                        selectedSenderFuente === row.cloud_guess
                          ? 'bg-cyan-50 dark:bg-cyan-400/10'
                          : 'hover:bg-gray-50 dark:hover:bg-slate-800'
                      }`}
                    >
                      <td className="px-5 py-3.5"><CloudPill vendor={row.cloud_guess} /></td>
                      <td className="px-5 py-3.5 font-bold text-gray-700 dark:text-slate-200 truncate">{row.domain}</td>
                      <td className="px-5 py-3.5 font-mono text-gray-500 dark:text-slate-400">{formatCompact(row.total_mensajes)}</td>
                      <td className="px-5 py-3.5"><ComplianceBar pct={row.pass_pct} /></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-400 italic text-xs">
                      No hay datos de nube.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {cumplimientoNube.length > 5 && (
              <button
                onClick={() => setIsNubeDrawerOpen(!isNubeDrawerOpen)}
                className="w-full py-3 px-4 bg-gray-50/80 dark:bg-slate-800/50 hover:bg-cyan-50 dark:hover:bg-cyan-950/40 text-cyan-600 dark:text-cyan-400 text-xs font-bold border-t border-gray-100 dark:border-slate-800 flex items-center justify-center gap-2 transition-colors"
              >
                <i className={`fas ${isNubeDrawerOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`}></i>
                <span>{isNubeDrawerOpen ? 'Plegar registros (mostrar solo 5)' : `Desplegar (+${cumplimientoNube.length - 5} registros más)`}</span>
              </button>
            )}
          </div>
        </div>
      </div>
        </div>
      </div>

        {/* Tablas de IPs (Resultados Filtrados) */}
          {/* Badge de Filtro Activo */}
          {selectedSenderFuente !== '' && (
            <div className="flex items-center gap-2 animate-in slide-in-from-left-4 duration-300">
              <span className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest">Filtro activo:</span>
              <div className="flex items-center gap-2 bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 px-3 py-1 rounded-full text-xs font-bold border border-cyan-200 dark:border-cyan-500/30">
                <i className="fas fa-filter text-[10px]"></i>
                {selectedSenderFuente}
                <button onClick={() => setSelectedSenderFuente('')} className="ml-1 hover:text-cyan-900 dark:hover:text-cyan-200 transition">
                  <i className="fas fa-times-circle"></i>
                </button>
              </div>
            </div>
          )}

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 dark:border-slate-800 bg-gray-50/20 dark:bg-slate-800/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="fas fa-check-circle text-emerald-500 text-sm"></i>
            <h3 className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest">IPs que cumplen DMARC</h3>
          </div>
          
          {loading ? <i className="fas fa-circle-notch animate-spin text-cyan-500"></i> :
          <button
            onClick={handleExportIpsOk}
            disabled={filteredIpsOk.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-bold text-xs border border-emerald-200/60 dark:border-emerald-800/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 disabled:opacity-40 transition cursor-pointer"
            title="Exportar detalles a CSV"
          >
            <i className={`fas fa-file-csv text-xs`}></i>
            <span>{'Exportar CSV'}</span>
          </button>
          }
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-max table-fixed">
            <colgroup>
              <col className="w-[80px]" />
              <col className="w-[110px]" />
              <col className="w-[130px]" />
              <col className="w-[120px]" />
              <col className="w-[70px]" />
              <col className="w-[70px]" />
              <col className="w-[60px]" />
              <col className="w-[60px]" />
              <col className="w-[90px]" />
              <col className="w-[80px]" />
              <col className="w-[80px]" />
              <col className="w-[100px]" />
            </colgroup>
            <thead>
              <tr className="bg-gray-50/50 dark:bg-slate-800/30 border-b border-gray-100 dark:border-slate-800">
                <SortableHeader label="Fuente" column="reportorgname" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="RID" column="recordid" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="IP" column="sourceip" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="Dominio" column="dominio" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="País" column="country_iso" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="Mensajes" column="count" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="DKIM" column="dkimaligned" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="SPF" column="spfaligned" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="Disposición" column="disposition" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="Auth" column="authtype" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="Resultado" column="result" sortConfig={sortConfigOk} onSort={handleSortOk} />
                <SortableHeader label="Selector" column="selector" sortConfig={sortConfigOk} onSort={handleSortOk} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
              {sortedAndPaginatedOk.length > 0 ? (
                sortedAndPaginatedOk.map((row, i) => (
                  <tr key={i} className="hover:bg-cyan-50/20 dark:hover:bg-cyan-400/5 transition text-[11px]">
                    <td className="px-3 py-3 overflow-hidden"><CloudPill vendor={row?.cloud_guess} /></td>
                    <td className="dark:md:hover:bg-cyan-800/20 hover:bg-cyan-50 px-3 py-3 font-mono font-bold text-cyan-600 dark:text-cyan-400 truncate"
                        onClick={() => window.open(`/record/${encodeURIComponent(row.recordid)}`)}>{row?.recordid}</td>
                    <td className="px-3 py-3 font-mono text-gray-700 dark:text-slate-300 truncate">{row?.sourceip}</td>
                    <td className="px-3 py-3 text-gray-600 dark:text-slate-400 truncate">{row?.dominio}</td>
                    <td className="px-3 py-3"><CountryFlagImg iso={row?.country_iso} /></td>
                    <td className="px-3 py-3 font-bold text-gray-700 dark:text-slate-200">{row?.count}</td>
                    <td className="px-2 py-3"><BoolPill value={row?.dkimaligned} /></td>
                    <td className="px-2 py-3"><BoolPill value={row?.spfaligned} /></td>
                    <td className="px-3 py-3 uppercase text-gray-500 dark:text-slate-500 font-bold truncate text-[10px]">{row?.disposition}</td>
                    <td className="px-3 py-3 uppercase text-gray-500 dark:text-slate-500 truncate text-[10px]">{row?.authtype}</td>
                    <td className="px-3 py-3 font-bold uppercase text-emerald-600 dark:text-emerald-500 truncate text-[10px]">{row?.result}</td>
                    <td className="px-3 py-3 font-mono text-gray-400 dark:text-slate-600 italic truncate text-[10px]">{row?.selector || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12} className="px-6 py-10 text-center text-gray-400 italic">
                    {loading ? 'Cargando datos...' : 'No hay registros para mostrar. Realice una consulta.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 bg-gray-50/50 dark:bg-slate-800/30 flex justify-between items-center border-t border-gray-100 dark:border-slate-800">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{filteredIpsOk.length} registros</span>
            <div className="flex items-center space-x-2">
              <button onClick={() => setPageOk(p => Math.max(1, p - 1))} disabled={pageOk === 1} className="p-1.5 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-30 transition text-gray-500"><i className="fas fa-chevron-left text-[10px]"></i></button>
              <span className="text-[10px] font-bold text-gray-500 mx-2">{pageOk} / {totalPagesOk}</span>
              <button onClick={() => setPageOk(p => Math.min(totalPagesOk, p + 1))} disabled={pageOk >= totalPagesOk} className="p-1.5 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-30 transition text-gray-500"><i className="fas fa-chevron-right text-[10px]"></i></button>
            </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 dark:border-slate-800 bg-gray-50/20 dark:bg-slate-800/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="fas fa-exclamation-triangle text-red-500 text-sm"></i>
            <h3 className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest">IPs que no cumplen DMARC</h3>
          </div>
          {loading ? <i className="fas fa-circle-notch animate-spin text-cyan-500"></i> :
          <button
            onClick={handleExportIpsBad}
            disabled={filteredIpsBad.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 font-bold text-xs border border-red-200/60 dark:border-red-800/40 hover:bg-red-100 dark:hover:bg-red-900/60 disabled:opacity-40 transition cursor-pointer"
            title="Exportar detalles a CSV"
          >
            <i className={`fas fa-file-csv text-xs`}></i>
            <span>{'Exportar CSV'}</span>
          </button>
          }
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-max table-fixed">
            <colgroup>
              <col className="w-[140px]" />
              <col className="w-[90px]" />
              <col className="w-[140px]" />
              <col className="w-[160px]" />
              <col className="w-[160px]" />
              <col className="w-[140px]" />
              <col className="w-[80px]" />
            </colgroup>
            <thead>
              <tr className="bg-gray-50/50 dark:bg-slate-800/30 border-b border-gray-100 dark:border-slate-800">
                <SortableHeader label="Fuente" column="cloud_guess" sortConfig={sortConfigBad} onSort={handleSortBad} />
                <SortableHeader label="País" column="country_iso" sortConfig={sortConfigBad} onSort={handleSortBad} />
                <SortableHeader label="IP" column="sourceip" sortConfig={sortConfigBad} onSort={handleSortBad} />
                <SortableHeader label="Dominio" column="header_from" sortConfig={sortConfigBad} onSort={handleSortBad} />
                <SortableHeader label="Remitente" column="sending_domain" sortConfig={sortConfigBad} onSort={handleSortBad} />
                <SortableHeader label="Organización" column="fuente" sortConfig={sortConfigBad} onSort={handleSortBad} />
                <SortableHeader label="Mensajes" column="mensajes_fail" sortConfig={sortConfigBad} onSort={handleSortBad} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
              {sortedAndPaginatedBad.length > 0 ? (
                sortedAndPaginatedBad.map((row, i) => (
                  <tr key={i} className="hover:bg-red-50/20 dark:hover:bg-red-400/5 transition text-[11px]">
                    <td className="px-3 py-3 overflow-hidden"><CloudPill vendor={row?.cloud_guess} /></td>
                    <td className="px-3 py-3"><CountryFlagImg iso={row?.country_iso} /></td>
                    <td className="dark:md:hover:bg-red-800/20 hover:bg-red-50 px-3 py-3 font-mono font-bold text-red-600 dark:text-red-400 truncate"
                        onClick={() => { window.open(`/detalle-ip-invalida?${new URLSearchParams({ip: row.ip.trim(),dominio,desde,hasta})}`).focus()}}
                    >
                      {row?.ip}
                    </td>
                    <td className="px-3 py-3 text-gray-600 dark:text-slate-400 truncate">{row?.header_from}</td>
                    <td className="px-3 py-3 text-gray-600 dark:text-slate-400 truncate">{row?.sending_domain}</td>
                    <td className="px-3 py-3 text-gray-500 dark:text-slate-500 italic truncate text-[10px]">{row?.fuente}</td>
                    <td className="px-3 py-3 font-bold text-red-600 dark:text-red-400">{row?.mensajes_fail}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-gray-400 italic">
                    {loading ? 'Cargando datos sospechosos...' : 'No hay registros sospechosos detectados.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 bg-gray-50/50 dark:bg-slate-800/30 flex justify-between items-center border-t border-gray-100 dark:border-slate-800">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{filteredIpsBad.length} alertas</span>
          <div className="flex items-center space-x-2">
            <button onClick={() => setPageBad(p => Math.max(1, p - 1))} disabled={pageBad === 1} className="p-1.5 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-30 transition text-gray-500"><i className="fas fa-chevron-left text-[10px]"></i></button>
            <span className="text-[10px] font-bold text-gray-500 mx-2">{pageBad} / {totalPagesBad}</span>
            <button onClick={() => setPageBad(p => Math.min(totalPagesBad, p + 1))} disabled={pageBad >= totalPagesBad} className="p-1.5 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-30 transition text-gray-500"><i className="fas fa-chevron-right text-[10px]"></i></button>
          </div>
        </div>
      </div>
      
      <ErrorModal 
        isOpen={errorModal.isOpen} 
        message={errorModal.message} 
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })} 
      />
    </div>
  );
};

export default DmarcDashboard;
